/**
 * Origin filter config for the Assets Overview toolbar dropdown.
 *
 * The "All Assets" dropdown filters by ASSET ORIGIN (not by industry). Per
 * asset-domain-v2-design.md §2.2.1: `All Assets` = every origin, `New Assets` =
 * only `ivt_incubated`. Kept as a config array (label + predicate) so future
 * origin buckets (e.g. Partner / Legacy) are added here, not hardcoded into the
 * component. The data itself is always the full `ListAssets({})` set — filtering
 * is purely client-side, so switching options never refetches and never diverges
 * from the KPI/distribution numbers (which are always over the full set).
 */
import { AssetOrigin, type AssetSummary } from '../../../client/IvtHubClient';

export interface OriginFilterOption {
  /** Stable id used as the Select option value. */
  id: string;
  /** Dropdown label. */
  label: string;
  /** Keep predicate; true = row is included under this filter. */
  match: (asset: AssetSummary) => boolean;
}

export const ORIGIN_FILTERS: OriginFilterOption[] = [
  { id: 'all', label: 'All Assets', match: () => true },
  // Case-insensitive: beta IVTHub currently serializes origin as the uppercase
  // enum NAME (IVT_INCUBATED) rather than the smithy lowercase value — see isNew
  // in assetGrouping.ts. Normalize so the "New Assets" filter matches either case.
  { id: 'new', label: 'New Assets', match: (a) => (a.origin ?? '').toLowerCase() === AssetOrigin.IVT_INCUBATED },
];

export const DEFAULT_ORIGIN_FILTER_ID = 'all';

/** Resolve an option by id, falling back to the default ("all") if unknown. */
export function getOriginFilter(id: string): OriginFilterOption {
  return ORIGIN_FILTERS.find((o) => o.id === id) ?? ORIGIN_FILTERS[0];
}

// ── Persistence (survive navigating away + back) ─────────────────────────────
// Session-scoped so the choice persists across in-app navigation but resets in a
// fresh tab. A single string id → plain sessionStorage read/write is enough (the
// page is the only reader; it remounts on return and seeds from here).
const ORIGIN_STORAGE_KEY = 'ivt.assets.originFilter';

export function loadOriginFilterId(): string {
  try {
    const v = sessionStorage.getItem(ORIGIN_STORAGE_KEY);
    if (v && ORIGIN_FILTERS.some((o) => o.id === v)) return v; // only accept known ids
  } catch {
    /* ignore */
  }
  return DEFAULT_ORIGIN_FILTER_ID;
}

export function saveOriginFilterId(id: string): void {
  try {
    sessionStorage.setItem(ORIGIN_STORAGE_KEY, id);
  } catch {
    /* ignore */
  }
}
